import { useCallback, useEffect, useState } from "react";
import { getAnonId, getHandle } from "./identity.js";

/**
 * Client view of a push-your-luck run (server-authoritative via daily_locks.streak — no
 * gate). Fetches the current run streak on mount and exposes spin(). The server applies
 * each spin's outcome to the stored streak (+1/x2/x3 grow it, bust → 0 ends the run).
 */

/** One step of a spin's outcome chain. Mirrors the server's Outcome (redeclared here to
 *  keep game-kit free of a game-api dependency). The chain's last entry is terminal. */
export type OutcomeKind = "+1" | "x2" | "again" | "bust" | "nothing" | "jackpot";
export interface Outcome {
  kind: OutcomeKind;
  streakAfter: number;
  /** Needle stop on the dial, 0..100 (flavor). */
  gaugePercent: number;
  label: string;
}

export interface SpinResult {
  /** The resolved chain (re-rolls + terminal). */
  outcomes: Outcome[];
  /** Run streak after the terminal outcome. */
  streak: number;
  rank: number;
  total: number;
  percentile: number;
}

export interface Run {
  /** Current run streak (0 after a bust or before the first spin). */
  streak: number;
  loading: boolean;
  /** Spin once. Returns null if no handle is set yet (caller should collect one). */
  spin: () => Promise<SpinResult | null>;
  refresh: () => Promise<void>;
}

export function useRun(game: string): Run {
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `/api/run?game=${encodeURIComponent(game)}&anonId=${encodeURIComponent(
          getAnonId(),
        )}`,
      );
      if (!res.ok) throw new Error(`run ${res.status}`);
      const d = (await res.json()) as { streak: number };
      setStreak(d.streak);
    } catch {
      // leave default (0) — a transient failure shouldn't block a spin
    } finally {
      setLoading(false);
    }
  }, [game]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const spin = useCallback(async (): Promise<SpinResult | null> => {
    const handle = getHandle();
    if (handle === null) return null; // caller collects a handle first
    const res = await fetch("/api/spin", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ game, anonId: getAnonId(), handle }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as SpinResult;
    setStreak(data.streak);
    return data;
  }, [game]);

  return { streak, loading, spin, refresh };
}
